import { Failure, Success } from "@pdfking/errors"
import { ArrayQ } from "@pdfking/structs"
import { Items, RecordMap } from "@pdfking/tfunc"

import type { ContainerError, ValidationResult } from "#public"

// A validated child also carries the `input` it was given.
type Child<T> = ValidationResult<T> & { input: unknown }

// Confirm a record/array of children all succeeded. If any failed, fails with a
// single `CONTAINER_ERROR` (same for list or record) carrying the failed-child
// `count` and `input` rebuilt from the children's own inputs (so it always
// reflects the live values; the per-child detail stays on the node's `results`).
// On success, unwraps to the plain values keeping shape.
export function ConfirmAllValid<T>(results: Child<T>[]): ValidationResult<T[], ContainerError>
export function ConfirmAllValid<T>(
  results: Record<string, Child<T>>
): ValidationResult<Record<string, T>, ContainerError>
export function ConfirmAllValid<T>(
  results: Record<string, Child<T>> | Child<T>[]
): ValidationResult<Record<string, T> | T[], ContainerError>
export function ConfirmAllValid<T>(
  results: Record<string, Child<T>> | Child<T>[]
): ValidationResult<Record<string, T> | T[], ContainerError> {
  if (ArrayQ(results)) {
    const failed = results.map((r, i) => [i, r] as const).filter(([, r]) => r.error)
    if (failed.length)
      return Failure("CONTAINER_ERROR", {
        count: failed.length,
        keys: failed.map(([i]) => i),
        input: results.map(r => r.input),
      })
    return Success(results.map(({ value }) => value as T))
  }

  const failed = Items(results).filter(([, { error }]) => error)
  const count = failed.length
  if (count)
    return Failure("CONTAINER_ERROR", {
      count,
      keys: failed.map(([k]) => k),
      input: RecordMap(r => r.input, results),
    })

  return Success(RecordMap(({ value }) => value as T, results))
}
